import { Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getBeautifiedLocalDate } from '@/core';
import { ClientVisitDetail } from '../types';
import NextCloudButton from '@/common/components/NextCloudButton';

type DetailsProps = {
  clientVisitDetail: ClientVisitDetail;
};

const Details = ({ clientVisitDetail }: DetailsProps) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {clientVisitDetail.isOpen && (
        <NextCloudButton
          woUid={clientVisitDetail?.workOrder?.uId || ''}
          visitUid={clientVisitDetail.uId || ''}
        />
      )}
      <Box sx={{ display: 'flex', gap: 3, justifyContent: 'space-between', mr: '25%' }}>
        <Box sx={{ display: 'flex', gap: 5 }}>
          <List sx={{ display: 'flex', flexDirection: 'column' }}>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Status', { defaultValue: 'Status' })}
                secondary={clientVisitDetail.status || '/'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Standards', { defaultValue: 'Standards' })}
                secondary={clientVisitDetail.standards || '/'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Planned time', { defaultValue: 'Planned time' })}
                secondary={clientVisitDetail.planedTime || '/'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Date start', { defaultValue: 'Date start' })}
                secondary={
                  clientVisitDetail.datStart
                    ? getBeautifiedLocalDate(clientVisitDetail.datStart)
                    : 'N/A'
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Date end', { defaultValue: 'Date end' })}
                secondary={
                  clientVisitDetail.datEnd
                    ? getBeautifiedLocalDate(clientVisitDetail.datEnd)
                    : 'N/A'
                }
              />
            </ListItem>
          </List>
          <List sx={{ display: 'flex', flexDirection: 'column' }}>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Num of non conformities', { defaultValue: 'Num of non conformities' })}
                secondary={clientVisitDetail.nonConform || '0'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Deadline for rectifying noncompliances', {
                  defaultValue: 'Deadline for rectifying noncompliances',
                })}
                secondary={
                  clientVisitDetail.ncSendDat
                    ? getBeautifiedLocalDate(clientVisitDetail.ncSendDat)
                    : 'N/A'
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Date of response to noncompliances', {
                  defaultValue: 'Date of response to noncompliances',
                })}
                secondary={
                  clientVisitDetail.recAnswerDat
                    ? getBeautifiedLocalDate(clientVisitDetail.recAnswerDat)
                    : 'N/A'
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Recommend', { defaultValue: 'Recommend' })}
                secondary={clientVisitDetail.recommend || '/'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Deadline for following recommendations', {
                  defaultValue: 'Deadline for following recommendations',
                })}
                secondary={
                  clientVisitDetail.recPlannedDat
                    ? getBeautifiedLocalDate(clientVisitDetail.recPlannedDat)
                    : 'N/A'
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Date of response', {
                  defaultValue: 'Date of response',
                })}
                secondary={
                  clientVisitDetail.ncAnswerDat
                    ? getBeautifiedLocalDate(clientVisitDetail.ncAnswerDat)
                    : 'N/A'
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('InvoiceNum', { defaultValue: 'Invoice number' })}
                secondary={clientVisitDetail.invoiceNumber || '/'}
              />
            </ListItem>
          </List>
        </Box>
        {clientVisitDetail?.services && (
          <List
            subheader={
              <Typography variant="h5" sx={{ padding: '8px 16px' }}>
                {t('Services', { defaultValue: 'Services' })}
              </Typography>
            }
          >
            {clientVisitDetail.services.map((service) => (
              <ListItem key={service.id}>
                <ListItemText
                  primaryTypographyProps={{ fontSize: '1.25rem' }}
                  secondaryTypographyProps={{ fontSize: '1rem' }}
                  primary={service.serviceShowAs}
                  secondary={
                    <Box>
                      <Typography sx={{ fontSize: '1rem', fontWeight: '500' }}>
                        {t('Standard', { defaultValue: 'Standard' })}: {service.processStandard},{' '}
                      </Typography>
                      {service.srvNotes && (
                        <Typography>
                          {t('Note', { defaultValue: 'Note' })}: {service.srvNotes}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default Details;
